"use client"

import * as React from "react"
import { Popover } from "@base-ui/react/popover"
import { Search, ChevronDown, Check } from "lucide-react"
import { filterBySearch } from "@/shared/lib/search-match"
import { cn } from "@/shared/lib/utils"

export interface SearchableSelectOption {
  value: string
  label: string
  searchText?: string
  keywords?: string[]
  disabled?: boolean
}

export interface SearchableSelectGroup {
  label: string
  options: SearchableSelectOption[]
}

interface SearchableSelectProps {
  value: string
  onValueChange: (value: string) => void
  groups: SearchableSelectGroup[]
  placeholder?: string
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  emptyMessage?: string
  loading?: boolean
  className?: string
  triggerClassName?: string
  contentClassName?: string
  disabled?: boolean
  closeOnSelect?: boolean
  /**
   * Supply a custom trigger element (e.g. an existing styled button) instead of
   * the default value/placeholder trigger. The element is merged onto the
   * Popover trigger via Base UI's `render`, so it inherits open/close handling,
   * ref, and aria props automatically.
   */
  renderTrigger?: React.ReactElement
  /** Notified whenever the dropdown opens or closes (e.g. to gate a fetch). */
  onOpenChange?: (open: boolean) => void
  isOptionSelected?: (option: SearchableSelectOption) => boolean
  renderOption?: (
    option: SearchableSelectOption,
    state: { selected: boolean }
  ) => React.ReactNode
  renderValue?: (option: SearchableSelectOption | null) => React.ReactNode
  "data-testid"?: string
  /** Forwarded to the trigger so a `<Label htmlFor>` associates with it. */
  id?: string
}

function SearchableSelect({
  value,
  onValueChange,
  groups,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  searchValue,
  onSearchChange,
  emptyMessage = "No matches",
  loading = false,
  className,
  triggerClassName,
  contentClassName,
  disabled,
  closeOnSelect = true,
  renderTrigger,
  onOpenChange,
  isOptionSelected,
  renderOption,
  renderValue,
  "data-testid": testId,
  id,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange],
  )
  const [internalSearch, setInternalSearch] = React.useState("")
  const searchRef = React.useRef<HTMLInputElement>(null)
  const search = searchValue ?? internalSearch

  React.useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        searchRef.current?.focus({ preventScroll: true })
      })
    }
  }, [open])

  const selectedOption = React.useMemo(() => {
    for (const g of groups) {
      const match = g.options.find((o) => o.value === value)
      if (match) return match
    }
    return null
  }, [groups, value])

  const filtered = React.useMemo(() => {
    if (onSearchChange) return groups
    if (!search.trim()) return groups
    return groups
      .map((g) => ({
        ...g,
        options: filterBySearch(g.options, search, (o) =>
          [o.label, o.searchText ?? "", ...(o.keywords ?? [])].join(" "),
        ),
      }))
      .filter((g) => g.options.length > 0)
  }, [groups, onSearchChange, search])

  const handleSelect = React.useCallback(
    (optionValue: string) => {
      onValueChange(optionValue)
      if (closeOnSelect) {
        setOpen(false)
        if (onSearchChange) {
          onSearchChange("")
        } else {
          setInternalSearch("")
        }
      }
    },
    [closeOnSelect, onSearchChange, onValueChange],
  )

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      {renderTrigger ? (
        <Popover.Trigger
          data-slot="searchable-select-trigger"
          data-testid={testId}
          id={id}
          disabled={disabled}
          render={renderTrigger}
        />
      ) : (
        <Popover.Trigger
          data-slot="searchable-select-trigger"
          data-testid={testId}
          id={id}
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between gap-1.5 rounded-field border border-input bg-transparent py-2 pr-2 pl-2.5 text-base whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-placeholder:text-muted-foreground dark:bg-input/30 dark:hover:bg-input/50",
            className,
            triggerClassName,
          )}
        >
          <div
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              !selectedOption && "text-muted-foreground",
            )}
          >
            {selectedOption
              ? (renderValue?.(selectedOption) ?? selectedOption.label)
              : placeholder}
          </div>
          <ChevronDown className="pointer-events-none size-4 shrink-0 text-muted-foreground" />
        </Popover.Trigger>
      )}

      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          sideOffset={4}
          align="start"
          className="isolate z-50"
        >
          <Popover.Popup
            data-slot="searchable-select-content"
            onKeyDown={(event) => event.stopPropagation()}
            className={cn(
              "w-(--anchor-width) min-w-[220px] origin-(--transform-origin) rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)] duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
              contentClassName,
            )}
          >
            <div className="border-b-[0.5px] border-[var(--rule)] p-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => {
                    const next = e.target.value
                    if (onSearchChange) {
                      onSearchChange(next)
                    } else {
                      setInternalSearch(next)
                    }
                  }}
                  placeholder={searchPlaceholder}
                  className="h-7 w-full rounded-md bg-[var(--bg-subtle)] pl-7 pr-2 text-[12px] text-[var(--ink-1)] outline-none placeholder:text-[var(--ink-3)]"
                />
              </div>
            </div>

            <div className="max-h-[240px] overflow-y-auto p-1">
              {loading && (
                <div className="px-2 py-4 text-center text-[12px] text-[var(--ink-3)]">
                  Searching...
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="px-2 py-4 text-center text-[12px] text-[var(--ink-3)]">
                  {emptyMessage}
                </div>
              )}
              {filtered.map((group, gi) => (
                <div key={group.label}>
                  {gi > 0 && (
                    <div className="mx-1 my-1 h-px bg-[var(--rule)]" />
                  )}
                  <div className="px-1.5 py-1 text-[11px] font-medium text-muted-foreground">
                    {group.label}
                  </div>
                  {group.options.map((option) => {
                    const selected = isOptionSelected
                      ? isOptionSelected(option)
                      : option.value === value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={option.disabled}
                        onClick={() => handleSelect(option.value)}
                        className={cn(
                          "relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1.5 pr-8 pl-2 text-left text-[13px] text-[var(--ink-2)] outline-hidden select-none hover:bg-[var(--bg-subtle)] focus:bg-[var(--bg-subtle)] disabled:pointer-events-none disabled:opacity-50",
                          renderOption && "items-start py-2 pr-9",
                          selected && "font-medium text-[var(--ink-1)]",
                        )}
                      >
                        {renderOption?.(option, { selected }) ?? option.label}
                        {selected && (
                          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
                            <Check className="size-3.5" />
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

export { SearchableSelect }
